import { useEffect, useMemo, useState } from 'react';
import { Inbox, Plus, Sparkles } from 'lucide-react';
import {
  Badge,
  Button,
  Input,
  Modal,
  Select,
  cn,
  useToast,
  type SelectOption,
} from '../../ui';
import {
  TASK_TYPES,
  blankTaskDraft,
  formatTaskEstimate,
  totalEffortDays,
  validateTaskDraft,
} from '../../features/tasks/taskModel';
import type { Task, TaskDraft, TaskType } from '../../features/tasks/types';
import type {
  CapacitySummary,
  EngineerLoad,
} from '../../features/sprintboard/engine/capacity';
import { PeopleInput } from '../common/PeopleInput';
import { usePeopleDirectory } from '../../features/teams/usePeopleDirectory';

export interface AddToSprintModalProps {
  open: boolean;
  onClose: () => void;
  sprintName: string;
  /** The room's backlog (top-level, unsprinted) tasks to pick from. */
  backlog: Task[];
  /**
   * The sprint's bandwidth-based capacity summary — used to preview a member's
   * occupied vs leftover bandwidth as work is assigned to them.
   */
  capacity: CapacitySummary;
  /** Sprint subtasks keyed by parent id, to compute a task's total effort. */
  subtasksByParent?: Map<string, Task[]>;
  /**
   * Default state of the "unplanned / ad-hoc" toggle — true when the sprint is
   * already running, so work added now is mid-sprint scope.
   */
  defaultAdhoc: boolean;
  /** True while a board write is in flight. */
  busy?: boolean;
  /** Adds an existing backlog task to the sprint (with the ad-hoc flag). */
  onAddFromBacklog: (task: Task, adhoc: boolean) => Promise<void>;
  /** Creates a new task directly in the sprint (with the ad-hoc flag). */
  onCreate: (draft: TaskDraft, adhoc: boolean) => Promise<Task>;
}

const TYPE_OPTIONS: SelectOption[] = TASK_TYPES.map((t) => ({
  value: t,
  label: t,
}));

type Mode = 'backlog' | 'new';

/**
 * A compact bandwidth preview for the assign flow: a member's current occupied
 * and leftover bandwidth and — when `addDays` > 0 — what leftover becomes after
 * this task's effort is added (e.g. "leftover 3.2d → 1.2d"). Leftover turns
 * amber at 0 and red when negative.
 */
function LeftoverPreview({
  load,
  addDays,
}: {
  load?: EngineerLoad;
  addDays: number;
}) {
  if (!load) return null;
  const projected = Math.round((load.remainingDays - addDays) * 100) / 100;
  const tone = (v: number) =>
    v < 0 ? 'text-red-300' : v === 0 ? 'text-amber-300' : 'text-emerald-300';
  return (
    <span className="inline-flex flex-wrap items-center gap-1 text-[11px] text-zinc-500">
      occupied{' '}
      <span className="tabular-nums text-zinc-300">{load.assignedDays}d</span> ·
      leftover{' '}
      <span className={cn('tabular-nums font-medium', tone(load.remainingDays))}>
        {load.remainingDays}d
      </span>
      {addDays > 0 && (
        <>
          <span aria-hidden="true">→</span>
          <span className={cn('tabular-nums font-medium', tone(projected))}>
            {projected}d
          </span>
        </>
      )}
    </span>
  );
}

/**
 * "Add task" to the current sprint. Two modes share one ad-hoc toggle:
 *   • From backlog — search the room's backlog and click a task to pull it in.
 *     The list stays open so several tasks can be added in a row; added tasks
 *     drop off as the backlog refreshes.
 *   • New task — a quick-create (title / type / owner) that lands straight in
 *     the sprint.
 * The "Mark as unplanned (ad-hoc)" toggle defaults on when the sprint is already
 * running, so scope-change metrics capture mid-sprint additions.
 */
export function AddToSprintModal({
  open,
  onClose,
  sprintName,
  backlog,
  capacity,
  subtasksByParent,
  defaultAdhoc,
  busy,
  onAddFromBacklog,
  onCreate,
}: AddToSprintModalProps) {
  const { toast } = useToast();
  const { people } = usePeopleDirectory();

  const [mode, setMode] = useState<Mode>('backlog');
  const [adhoc, setAdhoc] = useState(defaultAdhoc);
  const [query, setQuery] = useState('');
  // New-task fields.
  const [title, setTitle] = useState('');
  const [type, setType] = useState<TaskType>('Task');
  const [owner, setOwner] = useState('');
  const [effort, setEffort] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setMode(backlog.length > 0 ? 'backlog' : 'new');
    setAdhoc(defaultAdhoc);
    setQuery('');
    setTitle('');
    setType('Task');
    setOwner('');
    setEffort('');
  }, [open, defaultAdhoc, backlog.length]);

  /** Per-engineer bandwidth loads keyed by owner alias, for the assign preview. */
  const loadByAlias = useMemo(
    () => new Map<string, EngineerLoad>(capacity.perEngineer.map((l) => [l.alias, l])),
    [capacity],
  );

  /** Parses an optional non-negative number from a text field (blank → undefined). */
  const parseOptionalEffort = (raw: string): number | undefined => {
    const trimmed = raw.trim();
    if (!trimmed) return undefined;
    const n = Number(trimmed);
    return Number.isFinite(n) && n >= 0 ? n : undefined;
  };

  const filteredBacklog = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return backlog;
    return backlog.filter(
      (t) =>
        t.title.toLowerCase().includes(q) ||
        (t.owner ?? '').toLowerCase().includes(q),
    );
  }, [backlog, query]);

  const close = () => {
    if (submitting || busy) return;
    onClose();
  };

  const addExisting = async (task: Task) => {
    setSubmitting(true);
    try {
      await onAddFromBacklog(task, adhoc);
      toast({
        tone: 'success',
        title: adhoc ? 'Added as unplanned scope' : 'Added to sprint',
        description: task.title,
      });
    } catch (e) {
      toast({
        tone: 'error',
        title: 'Couldn’t add task',
        description: e instanceof Error ? e.message : 'Unknown error',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const createNew = async () => {
    const draft: TaskDraft = {
      ...blankTaskDraft('TODO'),
      title: title.trim(),
      type,
      owner: owner.trim() || undefined,
      // Mirror owner as assignee so existing board/filters keep working.
      assignee: owner.trim() || undefined,
      boardStatus: 'TODO',
      effortDays: parseOptionalEffort(effort),
    };
    const problems = validateTaskDraft(draft);
    if (problems.length > 0) {
      toast({ tone: 'error', title: 'Cannot create task', description: problems[0] });
      return;
    }
    setSubmitting(true);
    try {
      const created = await onCreate(draft, adhoc);
      toast({
        tone: 'success',
        title: adhoc ? 'Created as unplanned scope' : 'Task created in sprint',
        description: created.title,
      });
      onClose();
    } catch (e) {
      toast({
        tone: 'error',
        title: 'Create failed',
        description: e instanceof Error ? e.message : 'Unknown error',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const disabled = submitting || busy;

  return (
    <Modal
      open={open}
      onClose={close}
      size="lg"
      title={
        <span className="flex items-center gap-2">
          <Plus className="h-4 w-4 text-accent" aria-hidden="true" />
          Add task ·{' '}
          <span className="font-normal text-zinc-400">{sprintName}</span>
        </span>
      }
      footer={
        <>
          <Button variant="ghost" onClick={close} disabled={disabled}>
            {mode === 'backlog' ? 'Done' : 'Cancel'}
          </Button>
          {mode === 'new' && (
            <Button variant="primary" loading={submitting} disabled={busy} onClick={createNew}>
              Create in sprint
            </Button>
          )}
        </>
      }
    >
      <div className="space-y-4">
        {/* Mode toggle */}
        <div className="inline-flex rounded-xl border border-surface-border bg-surface-2 p-0.5">
          <button
            type="button"
            onClick={() => setMode('backlog')}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors',
              mode === 'backlog'
                ? 'bg-surface-3 text-zinc-100'
                : 'text-zinc-500 hover:text-zinc-300',
            )}
          >
            <Inbox className="h-4 w-4" aria-hidden="true" />
            Add from backlog
          </button>
          <button
            type="button"
            onClick={() => setMode('new')}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors',
              mode === 'new'
                ? 'bg-surface-3 text-zinc-100'
                : 'text-zinc-500 hover:text-zinc-300',
            )}
          >
            <Plus className="h-4 w-4" aria-hidden="true" />
            Create task in sprint
          </button>
        </div>

        {/* Ad-hoc toggle (shared by both modes) */}
        <button
          type="button"
          onClick={() => setAdhoc((v) => !v)}
          aria-pressed={adhoc}
          className={cn(
            'flex w-full items-center gap-3 rounded-xl border px-3.5 py-3 text-left transition-colors',
            adhoc
              ? 'border-amber-400/50 bg-amber-400/10'
              : 'border-surface-border bg-surface-1 hover:border-zinc-600',
          )}
        >
          <span
            className={cn(
              'relative h-5 w-9 shrink-0 rounded-full transition-colors',
              adhoc ? 'bg-amber-400' : 'bg-surface-3',
            )}
            aria-hidden="true"
          >
            <span
              className={cn(
                'absolute top-0.5 h-4 w-4 rounded-full bg-white transition-transform',
                adhoc ? 'translate-x-4' : 'translate-x-0.5',
              )}
            />
          </span>
          <span className="min-w-0">
            <span className="flex items-center gap-1.5 text-sm font-medium text-zinc-100">
              <Sparkles className="h-3.5 w-3.5 text-amber-300" aria-hidden="true" />
              Mark as unplanned (ad-hoc)
            </span>
            <span className="mt-0.5 block text-xs text-zinc-500">
              Surfaces this as scope added mid-sprint in the sprint header and
              insights.
            </span>
          </span>
        </button>

        {mode === 'backlog' ? (
          backlog.length === 0 ? (
            <p className="rounded-xl border border-dashed border-surface-border px-3 py-10 text-center text-sm text-zinc-500">
              The backlog is empty. Switch to “Create task in sprint” to add new
              work.
            </p>
          ) : (
            <div className="space-y-2">
              <Input
                value={query}
                placeholder="Search the backlog…"
                onChange={(e) => setQuery(e.target.value)}
              />
              <ul className="max-h-72 space-y-1.5 overflow-y-auto pr-1">
                {filteredBacklog.map((t) => {
                  const owner = (t.owner ?? '').trim();
                  const load = owner ? loadByAlias.get(owner) : undefined;
                  const addDays = totalEffortDays(t, subtasksByParent?.get(t.id));
                  return (
                  <li key={t.id}>
                    <button
                      type="button"
                      disabled={disabled}
                      onClick={() => void addExisting(t)}
                      className="flex w-full items-center gap-2 rounded-xl border border-surface-border bg-surface-1 px-3 py-2 text-left transition-colors hover:border-accent disabled:opacity-50"
                    >
                      <Plus className="h-4 w-4 shrink-0 text-accent" aria-hidden="true" />
                      <Badge tone="neutral">{t.type ?? 'Task'}</Badge>
                      <span className="min-w-0 flex-1 truncate text-sm font-medium text-zinc-100">
                        {t.title}
                      </span>
                      {t.owner && (
                        <span className="shrink-0 text-xs text-zinc-500">
                          {t.owner}
                        </span>
                      )}
                      <Badge
                        tone={
                          formatTaskEstimate(t) === 'Not Estimated'
                            ? 'neutral'
                            : 'accent'
                        }
                      >
                        {formatTaskEstimate(t)}
                      </Badge>
                    </button>
                    {load && (
                      <div className="mt-0.5 pl-9">
                        <LeftoverPreview load={load} addDays={addDays} />
                      </div>
                    )}
                  </li>
                  );
                })}
                {filteredBacklog.length === 0 && (
                  <li className="px-3 py-6 text-center text-xs text-zinc-600">
                    No backlog tasks match “{query}”.
                  </li>
                )}
              </ul>
            </div>
          )
        ) : (
          <div className="space-y-4">
            <Input
              label="Title"
              value={title}
              placeholder="Handle mid-sprint hotfix"
              onChange={(e) => setTitle(e.target.value)}
              hint="Just a title to start — add the rest later."
              autoFocus
            />
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Select
                label="Type"
                value={type}
                options={TYPE_OPTIONS}
                onChange={(e) => setType(e.target.value as TaskType)}
              />
              <div>
                <PeopleInput
                  label="Owner (alias)"
                  value={owner}
                  placeholder="jsmith"
                  people={people}
                  hint="Optional."
                  onChange={setOwner}
                />
              </div>
            </div>
            <Input
              label="Custom effort (days)"
              type="number"
              min={0}
              step={0.5}
              value={effort}
              placeholder="e.g. 2.5"
              hint="Optional. Counts toward the owner's occupied bandwidth."
              onChange={(e) => setEffort(e.target.value)}
            />
            {owner.trim() &&
              (() => {
                const load = loadByAlias.get(owner.trim());
                if (!load) return null;
                return (
                  <div className="rounded-xl border border-surface-border bg-surface-1 px-3 py-2">
                    <LeftoverPreview
                      load={load}
                      addDays={parseOptionalEffort(effort) ?? 0}
                    />
                  </div>
                );
              })()}
          </div>
        )}
      </div>
    </Modal>
  );
}

export default AddToSprintModal;
